// src/components/LiveChat.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentSlash, faArrowDown, faThumbtack } from "@fortawesome/free-solid-svg-icons";
import PinnedMessages, { togglePinMessageForSegment } from "./PinnedMessages";

const API = "https://api18081113.queroapp.pl";

/* ---------- HELPERS ---------- */
const stripSuffix = (name = "") => name.replace(/_[^_]+$/, "");
const prettyBase = (name = "", secretId = "") =>
  name.replace(new RegExp(`_${secretId}$`), "").replace(/_/g, " ").trim();

const stripSecretIdSuffix = (name = "", secretId = "") => {
  if (!name || !secretId) return name;
  const re = new RegExp(`_${secretId}$`);
  return name.replace(re, "");
};

const norm = (s) => (s ?? "").toString().trim().toLowerCase();

const nameVariants = (raw = "", secretId = "") => {
  const a = raw || "";
  const b = stripSuffix(a);
  const c = stripSecretIdSuffix(a, secretId);
  const d = stripSuffix(c);
  return Array.from(new Set([a, b, c, d].map(norm)));
};

const extractPrelegents = (json) => {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.prelegents)) return json.prelegents;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.segments)) return json.segments;
  return [];
};

const getSegName = (s) => s?.segment_name || s?.stage_name || s?.name || s?.segment || "";

const getGroupId = (s) => {
  const v = s?.group_id ?? s?.groupId ?? s?.group ?? null;
  if (typeof v === "string" && /^\d+$/.test(v)) return Number(v);
  return typeof v === "number" ? v : null;
};

const getPrelegentSegmentName = (p) =>
  p?.segment_name || p?.stage_name || p?.segment || p?.name || "";

const getPrelegentDisplay = (p) => ({
  name: p?.stage_name || p?.stage_name || p?.fullname || p?.full_name || p?.name || "Prelegent",
  avatar: p?.prelegent_img ?? null,
});

/* ---------- stripIds + prettyLabel (do UI bez secretId i stageId) ---------- */
const stripIds = (name = "", secretId = "") => {
  let s = name || "";
  const sid = (secretId || "").trim();
  if (!s) return s;

  let prev;
  do {
    prev = s;
    if (sid) s = s.replace(new RegExp(`_${sid}$`), "");
    s = s.replace(/_[a-z0-9]{6,16}$/i, ""); // usuń potencjalny stageId
  } while (s !== prev);

  return s;
};

const prettyLabel = (name = "", secretId = "") =>
  stripIds(name, secretId).replace(/_/g, " ").trim();

/* Pobierz najlepszą nazwę wyświetlaną dla pełnej nazwy segmentu:
   1) spróbuj znaleźć w prelegentByBase (stage/prelegent name)
   2) fallback -> prettyLabel */
const getSegmentDisplayName = (segmentFull, secretId, prelegentByBase) => {
  if (!segmentFull) return "";
  const baseClean = stripIds(segmentFull, secretId);
  const pre = prelegentByBase && prelegentByBase[baseClean];
  if (pre && pre.name) return pre.name;
  return prettyLabel(baseClean, secretId);
};

/* fingerprint wiadomości (na potrzeby highlightu / long-press) */
const buildMsgFingerprint = (msg) => {
  if (!msg) return "";
  const g = msg.guestid ?? "";
  const u = msg.username ?? "";
  const m = msg.message ?? "";
  return `${g}|${u}|${m}`;
};

/* sprawdzanie, czy wiadomość jest przypięta dla danego segmentu */
const isMessagePinned = (segmentKey, msg) => {
  if (!segmentKey || !msg || typeof window === "undefined") return false;
  try {
    const raw = localStorage.getItem(segmentKey);
    if (!raw) return false;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return false;
    const fp = buildMsgFingerprint(msg);
    return arr.some((m) => m._fp === fp || buildMsgFingerprint(m) === fp);
  } catch {
    return false;
  }
};

/* ---------- COMPONENT ---------- */
export default function LiveChat({ onTutorialMessagePinned, activeStage, refreshTrigger }) {
  const [messages, setMessages] = useState([]);
  const [tableName, setTableName] = useState(null);
  const [userColors] = useState({}); // (kolory dla userów – opcjonalne)

  const [prevActiveStage, setPrevActiveStage] = useState(null);
  /* activeStage passed via props */
  /* const [activeStage, setActiveStage] = useState(...) -> REMOVED */
  const [secretId, setSecretId] = useState(
    typeof window !== "undefined" ? localStorage.getItem("secretId") : null
  );

  // grupy / prelegenci
  const [groupId, setGroupId] = useState(null);
  const [groupSegments, setGroupSegments] = useState([]);
  const [selectedSegmentName, setSelectedSegmentName] = useState(null);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [prelegentByBase, setPrelegentByBase] = useState({});

  // ====== AUTOSCROLL ======
  const chatRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const isProgrammaticScrollRef = useRef(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [showJump, setShowJump] = useState(false);
  const AUTO_THRESHOLD = 48;

  // ====== LONG PRESS (pinowanie) ======
  const LONG_PRESS_MS = 450;
  const longPressTimerRef = useRef(null);
  const [pressedMsgFp, setPressedMsgFp] = useState(null);

  // ====== HOVER ======
  const [hoveredMsgFp, setHoveredMsgFp] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimerRef = useRef(null);

  // Kiedy zmieni się globalny activeStage (zmieniony przez Organizatora lub API), 
  // LiveChat zrzuca lokalnie zablokowany filtr podglądu prelekcji i wraca do bieżącego widoku:
  useEffect(() => {
    setSelectedSegmentName(null);
  }, [activeStage]);

  const isNearBottom = useCallback(
    (el) => {
      if (!el) return true;
      return el.scrollHeight - el.scrollTop - el.clientHeight <= AUTO_THRESHOLD;
    },
    [AUTO_THRESHOLD]
  );

  const isTutorialMode = !!onTutorialMessagePinned;

  /* Sync removed. Backend update handled by parent AdminDashboard */

  /* ---------- prelegenci ---------- */
  useEffect(() => {
    if (!secretId) {
      setPrelegentByBase({});
      return;
    }
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/get-prelegent-info/${secretId}`);
        const json = await res.json();
        const list = extractPrelegents(json);
        const map = {};
        list.forEach((p) => {
          const base = stripIds(getPrelegentSegmentName(p), secretId);
          const { name, avatar } = getPrelegentDisplay(p);
          if (base && !map[base]) map[base] = { name, avatar };
        });
        if (alive) setPrelegentByBase(map);
      } catch (e) {
        console.error("Błąd pobierania prelegentów:", e);
        if (alive) setPrelegentByBase({});
      }
    })();
    return () => {
      alive = false;
    };
  }, [secretId, refreshTrigger]);

  /* ---------- grupy (masters + non-masters) ---------- */
  useEffect(() => {
    if (!activeStage || !secretId) {
      setGroupId(null);
      setGroupSegments([]);
      setSelectedSegmentName(null);
      return;
    }

    let alive = true;
    const resToJsonSafe = async (res) => {
      try {
        return await res.json();
      } catch {
        return null;
      }
    };

    const load = async () => {
      setLoadingSegments(true);
      try {
        const mastersRes = await fetch(`${API}/segments-with-master/${secretId}`);
        const mastersJson = mastersRes.ok ? await mastersRes.json() : null;
        const masters = Array.isArray(mastersJson)
          ? mastersJson
          : Array.isArray(mastersJson?.segments)
            ? mastersJson.segments
            : [];

        const activeForms = nameVariants(activeStage, secretId);
        const masterForActive = masters.find((s) => {
          const forms = nameVariants(getSegName(s), secretId);
          return forms.some((f) => activeForms.includes(f));
        });

        const gid = getGroupId(masterForActive);
        if (!gid) {
          setGroupId(null);
          setGroupSegments([]);
          setSelectedSegmentName(null);
          return;
        }
        setGroupId(gid);

        const nonRes = await fetch(`${API}/segments-without-master/${secretId}`);
        const nonJson = nonRes.ok ? await resToJsonSafe(nonRes) : null;
        const nonMasters = Array.isArray(nonJson)
          ? nonJson
          : Array.isArray(nonJson?.segments)
            ? nonJson.segments
            : [];

        const inGroup = nonMasters.filter((s) => getGroupId(s) === gid);
        setGroupSegments(inGroup);

        setSelectedSegmentName((prev) =>
          prev && !inGroup.find((s) => getSegName(s) === prev) ? null : prev
        );
      } catch (e) {
        console.error("Błąd pobierania danych grupy:", e);
        setGroupId(null);
        setGroupSegments([]);
        setSelectedSegmentName(null);
      } finally {
        if (alive) setLoadingSegments(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [activeStage, secretId]);

  /* ---------- baza/tabela ---------- */
  const displayBaseName = selectedSegmentName ? stripSuffix(selectedSegmentName) : activeStage;

  useEffect(() => {
    if (!displayBaseName) {
      setTableName(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const partialForApi = stripSecretIdSuffix(displayBaseName, secretId || "");
        const res = await fetch(
          `${API}/find-table?partialName=${encodeURIComponent(partialForApi)}`
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Nie znaleziono tabeli");
        if (alive) setTableName(data.tableName);
      } catch (err) {
        console.error("Błąd wyszukiwania tabeli:", err);
        if (alive) setTableName(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [displayBaseName, secretId]);

  /* ---------- polling wiadomości ---------- */
  useEffect(() => {
    if (!tableName) return;
    let alive = true;
    const fetchMessages = async () => {
      try {
        const res = await fetch(`${API}/messages/${tableName}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Błąd pobierania wiadomości");
        if (alive) setMessages(data.messages || []);
      } catch (err) {
        console.error("Błąd pobierania wiadomości:", err);
      }
    };
    fetchMessages();
    const interval = setInterval(fetchMessages, 2000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [tableName]);

  /* ---------- AUTOSCROLL: interakcje ---------- */
  const disableAutoScroll = useCallback(() => {
    if (!autoScroll) return;
    setAutoScroll(false);
    setShowJump(true);
  }, [autoScroll]);

  const scrollToBottom = useCallback(
    (behavior = "auto") => {
      const el = chatRef.current;
      if (!el) return;

      isProgrammaticScrollRef.current = true;

      el.scrollTo({ top: el.scrollHeight, behavior });

      // Update ref immediately to prevent race conditions
      lastScrollTopRef.current = el.scrollHeight;
      setShowJump(false);
      setAutoScroll(true);

      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
        // Forced restart of autoScroll just in case
        setAutoScroll(true);
      }, behavior === "smooth" ? 600 : 150);
    },
    []
  );

  const handleScroll = useCallback(() => {
    const el = chatRef.current;
    if (!el) return;

    if (isProgrammaticScrollRef.current) {
      lastScrollTopRef.current = el.scrollTop;
      return;
    }

    const prev = lastScrollTopRef.current;
    const curr = el.scrollTop;

    // Update ref immediately so next check is accurate
    lastScrollTopRef.current = curr;

    // Detect explicit scroll UP
    // tolerance 2px to avoid jitter/bounce
    if (curr < prev - 2) {
      disableAutoScroll();
      // CRITCAL: If user scrolled UP, do not check "atBottom" logic to re-enable!
      return;
    }

    const atBottom = isNearBottom(el);

    if (atBottom) {
      // Only re-enable if we are effectively AT the bottom (and didn't just scroll up)
      if (!autoScroll) setAutoScroll(true);
      if (showJump) setShowJump(false);
    } else {
      // Not at bottom, user might have scrolled up previously or is somewhere in middle
      if (!showJump && !autoScroll) setShowJump(true);
    }
  }, [autoScroll, showJump, disableAutoScroll, isNearBottom]);

  useEffect(() => {
    if (autoScroll) {
      const smooth = messages?.length > 1 ? "smooth" : "auto";
      scrollToBottom(smooth);
    }
  }, [messages, autoScroll, scrollToBottom]);

  const handleToggleSegment = (fullName) => {
    setSelectedSegmentName((curr) => (curr === fullName ? null : fullName));
    requestAnimationFrame(() => {
      scrollToBottom("auto");
    });
  };

  /* ---------- LONG PRESS handlers ---------- */
  /* Long Press removed for simplicity - using explicit buttons instead */

  /* ---------- UI: Avatar ---------- */
  const RoundAvatar = ({ active, name, src, onClick, title }) => {
    const [broken, setBroken] = useState(false);
    const size = 64;
    const borderColor = active ? "#16a34a" : "#000000";
    const filter = active ? "none" : "grayscale(100%)";
    const ring = active ? "0 0 0 2px rgba(22,163,74,0.5)" : "none";
    const letter = (name || "").trim().charAt(0).toUpperCase() || "P";
    const showImage = !!src && !broken;

    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        {showImage ? (
          <img
            src={src}
            alt={name}
            width={size}
            height={size}
            onError={() => setBroken(true)}
            style={{
              width: size,
              height: size,
              objectFit: "cover",
              borderRadius: "9999px",
              border: `2px solid ${borderColor}`,
              filter,
              boxShadow: ring,
              userSelect: "none",
            }}
          />
        ) : (
          <div
            aria-label={name}
            style={{
              width: size,
              height: size,
              borderRadius: "9999px",
              border: `2px solid ${borderColor}`,
              background: "#e5e7eb",
              filter,
              boxShadow: ring,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 22,
              color: "#111827",
              userSelect: "none",
            }}
          >
            {letter}
          </div>
        )}
        <div
          style={{
            maxWidth: 120,
            textAlign: "center",
            fontSize: 12,
            lineHeight: 1.2,
            color: active ? "#111827" : "#374151",
            userSelect: "none",
          }}
        >
          {name}
        </div>
      </button>
    );
  };

  /* -------------- ACTIVE USERS COUNTER -------------- */
  const [activeCount, setActiveCount] = useState(0);
  useEffect(() => {
    if (!secretId) return;
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API}/get-active-guest-count/${secretId}`);
        const data = await res.json();
        if (data.count !== undefined) {
          console.log("Active users update:", data.count);
          setActiveCount(data.count);

          // Local Peak Tracking
          const peakKey = `peak_active_${secretId}`;
          const currentPeak = parseInt(localStorage.getItem(peakKey) || '0', 10);
          if (data.count > currentPeak) {
            localStorage.setItem(peakKey, data.count.toString());
          }
        }
      } catch (e) { }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 15000); // Poll every 15s
    return () => clearInterval(interval);
  }, [secretId]);

  /* ---------- RENDER ---------- */
  if (!activeStage) {
    return (
      <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center p-4">
        <div className="p-5 text-secondary w-100">
          <div className="mb-4 text-primary" style={{ opacity: 0.15 }}>
            <FontAwesomeIcon icon={faCommentSlash} style={{ fontSize: '4rem' }} />
          </div>
          <h5 className="fw-bold text-dark mb-3">Tu pojawią się pytania</h5>
          <p className="text-muted mb-4 small">
            Gdy goście wyślą wiadomość, zobaczysz ją tutaj w czasie rzeczywistym.
          </p>
          <div className="mt-4 p-4 bg-white shadow-md rounded-lg text-start border border-light">
            <h6 className="fw-bold text-primary mb-2 small text-uppercase" style={{ letterSpacing: '0.5px' }}>Szybki start:</h6>
            <ol className="small text-muted ps-3 mb-0">
              <li className="mb-2">Wybierz segment z listy po prawej</li>
              <li className="mb-2">Kliknij <strong className="text-primary">"WŁĄCZ"</strong></li>
              <li>Czekaj na pierwsze pytania gości</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  const avatars = (groupId && groupSegments.length > 0 ? groupSegments : []).map((s) => {
    const full = getSegName(s);
    const baseClean = stripIds(full, secretId);
    const pl =
      prelegentByBase[baseClean] || { name: prettyLabel(baseClean, secretId || ""), avatar: null };
    const src = pl.avatar ? `https://eventicon.queroapp.pl/prelegent_photo/${pl.avatar}` : null;
    return { full, name: pl.name, avatar: src, active: selectedSegmentName === full };
  });

  const displayTitle = getSegmentDisplayName(
    selectedSegmentName ? selectedSegmentName : activeStage || "",
    secretId || "",
    prelegentByBase
  );

  const pinnedSegmentKey = selectedSegmentName || activeStage || "";

  return (
    <div className="d-flex flex-column h-100" style={{ userSelect: "none" }}>

      {/* HEADER (White Card) */}
      <div className="bg-white shadow-md rounded-lg mb-3 px-4 py-3 flex-shrink-0 border border-light">
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ minWidth: 0 }} className="text-start">
            <h6 className="text-secondary fw-bold text-uppercase small mb-1 text-start" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
              Wiadomości z segmentu:
            </h6>
            <h5 className="fw-bold text-dark m-0 pb-1 text-truncate lh-sm text-start" title={displayTitle}>
              {displayTitle}
            </h5>
          </div>
          {/* ACTIVE USERS INDICATOR */}
          {activeCount > 0 && (
            <div className="d-flex align-items-center bg-light px-2 py-1 ms-2 rounded-pill border border-light flex-shrink-0" title="Aktywni goście (ostatnie 60s)">
              <div className="rounded-circle bg-success me-2" style={{ width: 8, height: 8, animation: 'pulse-green 2s infinite' }}></div>
              <span className="fw-bold text-success small" style={{ fontSize: '0.75rem' }}>{activeCount}</span>
            </div>
          )}
        </div>

        {groupId && avatars.length > 0 && (
          <div className="mt-3 border-top pt-2">
            <div className="d-flex align-items-center gap-3 overflow-x-auto nice-scroll pb-1">
              {avatars.map((a) => (
                <RoundAvatar
                  key={a.full}
                  active={a.active}
                  name={a.name}
                  src={a.avatar}
                  onClick={() => handleToggleSegment(a.full)}
                  title={`Segment grupy #${groupId}`}
                />
              ))}
              <div className="vr mx-1 bg-secondary opacity-25"></div>
              <RoundAvatar
                active={selectedSegmentName === null}
                name="Pytania ogólne"
                src={null}
                onClick={() => {
                  setSelectedSegmentName(null);
                  requestAnimationFrame(() => scrollToBottom("auto"));
                }}
                title="Pokaż wszystkie wiadomości"
              />
            </div>
          </div>
        )}
      </div>

      {/* PINNED MESSAGES (transparent container, component handles its own style) */}
      <div className="flex-shrink-0 mb-3">
        <PinnedMessages className="pinned_main w-100 bg-white shadow-sm rounded-4 border-0" segmentFullName={pinnedSegmentKey} />
      </div>

      {/* MESSAGES LIST (White Card Wrapper) */}
      <div
        className="bg-white shadow-md rounded-lg flex-grow-1 overflow-hidden d-flex flex-column border border-light"
        style={{ minHeight: 0 }}
      >
        <div
          ref={chatRef}
          onScroll={handleScroll}
          className="flex-grow-1 overflow-y-auto px-4 py-3 nice-scroll"
          style={{ scrollBehavior: "smooth", position: "relative" }}
        >
          {messages.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center py-5 opacity-50"
              style={{ textAlign: "center" }}
            >
              <FontAwesomeIcon className="fs-1 mb-3 text-primary" icon={faCommentSlash} style={{ opacity: 0.3 }} />
              <h6 className="fw-bold text-dark m-0">Brak pytań w tym segmencie</h6>
              <p className="small text-muted m-0 mt-1">Gdy goście coś napiszą, zobaczysz to tutaj.</p>
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => {
                const fp = buildMsgFingerprint(msg);
                const pinned = isMessagePinned(pinnedSegmentKey, msg);

                return (
                  <div
                    key={idx}
                    className="message-card p-3 bg-white rounded-lg shadow-sm border border-light mb-3 d-flex justify-content-between align-items-center transition-standard"
                    style={{ borderLeft: pinned ? '4px solid var(--primary-color)' : '1px solid var(--border-color)' }}
                  >
                    <div className="flex-grow-1 pe-3">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <strong
                          style={{ color: userColors[msg.guestid] || "var(--text-main)", fontSize: '0.9rem' }}
                        >
                          {msg.username}
                        </strong>
                        <span className="text-muted mx-1" style={{ fontSize: '0.7rem' }}>•</span>
                        <span className="text-muted" style={{ fontSize: '0.7rem' }}>teraz</span>
                      </div>
                      <div className="message-content text-dark" style={{ fontSize: '1rem', lineHeight: '1.5' }}>
                        {msg.message}
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePinMessageForSegment(pinnedSegmentKey, msg);
                          if (onTutorialMessagePinned) onTutorialMessagePinned();
                        }}
                        className={`btn btn-sm fw-bold px-3 py-2 rounded-3 shadow-sm border-2 transition-all ${pinned ? 'btn-warning text-white border-warning' : 'btn-outline-primary bg-white hover-bg-primary'}`}
                        style={{ fontSize: '0.75rem', minWidth: '100px' }}
                      >
                        <FontAwesomeIcon icon={faThumbtack} className={`${pinned ? '' : 'text-primary'} me-2`} />
                        {pinned ? 'ODPNIJ' : 'PRZYPNIJ'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {showJump && (
            <div
              style={{
                position: "sticky",
                bottom: 8,
                display: "flex",
                justifyContent: "flex-end",
                pointerEvents: "none",
                zIndex: 2,
              }}
            >
              <button
                type="button"
                onClick={() => scrollToBottom("smooth")}
                className="btn btn-light shadow-md rounded-pill d-flex align-items-center gap-2 border"
                style={{ pointerEvents: "auto", padding: '8px 16px' }}
              >
                <FontAwesomeIcon icon={faArrowDown} />
                <span className="small fw-bold">Na dół</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .message-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); border-color: var(--primary-color) !important; }
        .hover-bg-primary:hover { background-color: var(--primary-color) !important; color: white !important; }
        .hover-bg-primary:hover .text-primary { color: white !important; }
        @keyframes pulse-green {
          0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4); }
          70% { box-shadow: 0 0 0 6px rgba(37, 99, 235, 0); }
          100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
        }
        .animate-pulse { animation: pulse-green 2s infinite; }
        .bg-soft { background-color: var(--bg-soft); }
      `}</style>
    </div>
  );
}
